import React, { useMemo } from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { getDefaultDateRangeJson } from "src/base-components/FilterPopover/constants";
import { FieldsDropdown } from "src/base-components/FilterPopover/FieldsDropdown";
import { JunctionDropdown } from "src/base-components/FilterPopover/JunctionDropdown";
import {
  resolveOperatorOptions,
  resolveValueRenderer,
} from "src/base-components/FilterPopover/resolve";
import {
  FilterConditionMode,
  FilterConfig,
  FilterFormData,
  Junction,
  OperatorOption,
} from "src/base-components/FilterPopover/types";
import { isFieldDefinition } from "src/base-components/FilterPopover/utils";
import { OperatorDropdownInput } from "src/base-components/FilterPopover/ValueRenderers";
import { OPERATORS } from "src/constants/operators";
import { IconButton } from "src/design-system/Icon";

const PLACEHOLDER_OPERATOR_OPTIONS: OperatorOption[] = [
  { key: "eq", value: OPERATORS.eq },
];

const isDateField = (type: string | undefined): boolean =>
  type === "date" || type === "datetime";

type FilterConditionProps = Pick<
  FilterConfig,
  | "fields"
  | "sections"
  | "valueRenderers"
  | "allowedJunctions"
  | "operatorOptions"
  | "fieldOverrides"
> & {
  groupIndex: number;
  conditionIndex: number;
  mode: FilterConditionMode;
  onRemove: () => void;
  onJunctionChange?: (junction: Junction) => void;
  canRemove?: boolean;
};

export const FilterCondition: React.FC<FilterConditionProps> = ({
  fields: fieldOptions,
  sections,
  valueRenderers,
  operatorOptions,
  fieldOverrides,
  groupIndex,
  conditionIndex,
  mode,
  onRemove,
  onJunctionChange,
  canRemove = true,
  allowedJunctions,
}) => {
  const form = useFormContext<FilterFormData>();

  const selectedField = form.watch(
    `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.field`,
  );

  const fields = fieldOptions.filter(isFieldDefinition);

  const selectedFieldDef = useMemo(
    () => fields.find((f) => f.key === selectedField),
    [selectedField, fields],
  );

  const resolvedOperatorOptions = useMemo(
    () =>
      resolveOperatorOptions(selectedFieldDef, fieldOverrides, operatorOptions),
    [selectedFieldDef, fieldOverrides, operatorOptions],
  );

  const ValueRenderer = useMemo(
    () =>
      resolveValueRenderer(selectedFieldDef, fieldOverrides, valueRenderers),
    [selectedFieldDef, fieldOverrides, valueRenderers],
  );

  const handleFieldChange = (newField: string, currentField: string) => {
    if (currentField === newField || newField === "") return;

    const newFieldDef = fields.find((f) => f.key === newField);
    const fieldOperatorOpts = resolveOperatorOptions(
      newFieldDef,
      fieldOverrides,
      operatorOptions,
    );
    const conditionPath =
      `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}` as const;

    form.setValue(
      `${conditionPath}.operator`,
      fieldOperatorOpts[0]?.key ?? "eq",
    );
    form.setValue(
      `${conditionPath}.value`,
      isDateField(newFieldDef?.type) ? getDefaultDateRangeJson() : "",
    );
  };

  return (
    <div className="flex items-center gap-x-2">
      <div
        className={twJoin(
          "shrink-0 self-start",
          allowedJunctions?.length === 1 ? "w-12" : "w-[5rem]",
        )}
      >
        {mode === "where" ? (
          <p className="flex h-8 items-center font-inter-medium-12px text-gray-700">
            Where
          </p>
        ) : allowedJunctions?.length === 1 ? (
          <p className="flex h-8 items-center font-inter-medium-12px text-gray-700">
            {allowedJunctions[0]}
          </p>
        ) : (
          <Controller
            name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.junction`}
            render={({ field }) => (
              <JunctionDropdown
                dataLoc={`group-${groupIndex}-condition-${conditionIndex}-junction`}
                disabled={conditionIndex > 1}
                selectedKey={field.value}
                onSelect={(key) => {
                  field.onChange(key);
                  onJunctionChange?.(key as Junction);
                }}
              />
            )}
          />
        )}
      </div>
      <div className="flex w-full gap-x-2">
        <div className="flex-2">
          <Controller
            name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.field`}
            render={({ field }) => (
              <FieldsDropdown
                dataLoc={`group-${groupIndex}-condition-${conditionIndex}-field`}
                options={fieldOptions}
                sections={sections}
                selectedKey={field.value}
                onSelect={(newField) => {
                  handleFieldChange(newField, field.value);
                  field.onChange(newField);
                }}
              />
            )}
          />
        </div>
        <div className="flex-3">
          <Controller
            name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.value`}
            render={({ field, fieldState }) => {
              if (!ValueRenderer || !selectedFieldDef) {
                return (
                  <OperatorDropdownInput
                    conditionIndex={conditionIndex}
                    disabled
                    groupIndex={groupIndex}
                    operatorOptions={PLACEHOLDER_OPERATOR_OPTIONS}
                    onValueChange={() => {}}
                  />
                );
              }

              const currentOperator = form.getValues(
                `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator`,
              );

              return (
                <ValueRenderer
                  conditionIndex={conditionIndex}
                  dataLocPrefix={`group-${groupIndex}-condition-${conditionIndex}`}
                  error={fieldState.error?.message}
                  field={selectedFieldDef}
                  groupIndex={groupIndex}
                  operator={currentOperator}
                  operatorOptions={resolvedOperatorOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              );
            }}
          />
        </div>
        {canRemove && (
          <div className="flex h-8 items-center">
            <IconButton
              dataLoc={`group-${groupIndex}-condition-${conditionIndex}-delete`}
              icon={faTrashCan}
              size="2xs"
              onClick={onRemove}
            />
          </div>
        )}
      </div>
    </div>
  );
};
